import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteUser,
  type FirmProfile,
  getActingFirmId,
  inviteUser,
  listFirms,
  listUsers,
  parseTokenClaims,
  updateUser,
  UserRecord,
  UserRole,
} from "../api";

// Roles a firm ADMIN may assign. A SUPERADMIN additionally gets SUPERADMIN
// (the backend rejects SUPERADMIN-grants from non-platform callers with 403).
const FIRM_ROLES: UserRole[] = ["ADMIN", "OPERATOR", "DRIVER", "VIEWER"];

function generatePassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  let out = "";
  const buf = new Uint32Array(12);
  crypto.getRandomValues(buf);
  for (const n of buf) out += chars[n % chars.length];
  return out;
}

export function UsersPage() {
  const { t } = useTranslation(["dashboard", "common"]);
  const claims = parseTokenClaims();
  const role = claims?.role;
  const isSuper = role === "SUPERADMIN";
  // SUPERADMIN manages users too; in "act as a firm" mode they behave exactly
  // like that firm's admin. The gateway scopes every call to the acting firm.
  const isAdmin = role === "ADMIN" || isSuper;
  const actingFirm = isSuper ? getActingFirmId() : null;
  // In "All firms" mode (SUPERADMIN, no firm picked) we list every firm's users
  // and require an explicit firm choice before creating one.
  const allFirmsMode = isSuper && !actingFirm;
  const myFirm = actingFirm ?? claims?.firm_id ?? "";

  const [users, setUsers] = useState<UserRecord[]>([]);
  const [firms, setFirms] = useState<FirmProfile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role_, setRole] = useState<UserRole>("VIEWER");
  const [targetFirm, setTargetFirm] = useState("");
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);

  // SUPERADMIN can also grant the SUPERADMIN role; firm admins cannot.
  const assignableRoles: UserRole[] = isSuper
    ? ["SUPERADMIN", ...FIRM_ROLES]
    : FIRM_ROLES;

  async function refresh() {
    try {
      // All-firms mode: omit firm_id to fetch the aggregate roster.
      setUsers(await listUsers(allFirmsMode ? undefined : myFirm));
      setError(null);
    } catch (exc) {
      setError(String(exc));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isAdmin) {
      refresh();
      if (allFirmsMode) listFirms().then(setFirms).catch(() => undefined);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onInvite(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setInfo(null);
    setGeneratedPassword(null);
    const firmId = allFirmsMode ? targetFirm : myFirm;
    if (!firmId) {
      setError(t("users.selectFirmFirst"));
      return;
    }
    try {
      if (email) {
        // E-mail present → token invite link (no plaintext password leaves us).
        const created = await inviteUser({
          firm_id: firmId,
          username,
          display_name: displayName || undefined,
          email,
          phone: phone || undefined,
          role: role_,
        });
        if (created.invitation_email_sent === false) {
          setInfo(t("users.inviteEmailFailed"));
        } else {
          setInfo(t("users.inviteEmailSent", { email }));
        }
      } else {
        // No e-mail → fall back to an admin-set temporary password to share.
        const password = generatePassword();
        await inviteUser({
          firm_id: firmId,
          username,
          display_name: displayName || undefined,
          phone: phone || undefined,
          role: role_,
          password,
        });
        setGeneratedPassword(password);
      }
      setUsername("");
      setDisplayName("");
      setEmail("");
      setPhone("");
      setRole("VIEWER");
      refresh();
    } catch (exc) {
      setError(String(exc));
    }
  }

  // Change an existing user's role straight from the list (SUPERADMIN can pick
  // SUPERADMIN too). Own row is locked to avoid self-lockout (see render).
  async function onChangeRole(user: UserRecord, newRole: UserRole) {
    if (newRole === user.role) return;
    setError(null);
    setInfo(null);
    const ok = window.confirm(
      t("users.confirmRole", { username: user.username, role: newRole }),
    );
    if (!ok) {
      refresh(); // revert the select back to the stored value
      return;
    }
    try {
      await updateUser(user, { role: newRole });
      setInfo(t("users.roleChanged", { username: user.username, role: newRole }));
      refresh();
    } catch (exc) {
      setError(String(exc));
      refresh();
    }
  }

  async function onDelete(user: UserRecord) {
    if (user.id === claims?.user_id) {
      setError(t("users.cannotDeleteSelf"));
      return;
    }
    const ok = window.confirm(t("users.confirmDelete", { username: user.username }));
    if (!ok) return;
    try {
      await deleteUser(user.id);
      setInfo(t("users.deleted", { username: user.username }));
      refresh();
    } catch (exc) {
      setError(String(exc));
    }
  }

  if (!isAdmin) {
    return (
      <div className="page">
        <h2>{t("users.title")}</h2>
        <p className="error">{t("users.adminOnly")}</p>
      </div>
    );
  }

  return (
    <div className="page">
      <h2>{t("users.title")}</h2>
      <p className="muted">
        {allFirmsMode
          ? t("users.allFirmsScope")
          : t("common:shell.firm", { id: myFirm })}
      </p>
      {error && <p className="error">{error}</p>}
      {info && <p className="info">{info}</p>}

      {generatedPassword && (
        <div className="invite-banner">
          <strong>{t("users.tempPasswordLabel")}</strong>{" "}
          <code>{generatedPassword}</code>
          <br />
          <small>{t("users.tempPasswordHint")}</small>
        </div>
      )}

      <form onSubmit={onInvite} className="row-form grid">
        {allFirmsMode && (
          <select
            value={targetFirm}
            onChange={(e) => setTargetFirm(e.target.value)}
            required
          >
            <option value="">{t("users.selectFirm")}</option>
            {firms.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        )}
        <input placeholder={t("users.usernamePlaceholder")} value={username}
               onChange={(e) => setUsername(e.target.value)} required />
        <input placeholder={t("users.displayNamePlaceholder")} value={displayName}
               onChange={(e) => setDisplayName(e.target.value)} />
        <input placeholder={t("users.emailPlaceholder")} type="email" value={email}
               onChange={(e) => setEmail(e.target.value)} />
        <input placeholder={t("users.phonePlaceholder")} value={phone}
               onChange={(e) => setPhone(e.target.value)} />
        <select value={role_} onChange={(e) => setRole(e.target.value as UserRole)}>
          {assignableRoles.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button type="submit">{t("common:actions.invite")}</button>
      </form>

      {loading ? (
        <p>{t("common:actions.loading")}</p>
      ) : users.length === 0 ? (
        <p>{t("users.empty")}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t("users.th.user")}</th>
              <th>{t("users.th.name")}</th>
              {allFirmsMode && <th>{t("firms.fields.id")}</th>}
              <th>{t("users.th.email")}</th>
              <th>{t("users.th.role")}</th>
              <th>{t("users.th.active")}</th>
              <th>{t("users.th.action")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.username}{u.id === claims?.user_id && <span className="muted"> {t("users.you")}</span>}</td>
                <td>{u.display_name ?? "-"}</td>
                {allFirmsMode && <td className="muted small">{u.firm_id}</td>}
                <td>{u.email ?? "-"}</td>
                <td>
                  {u.id === claims?.user_id ? (
                    // Can't change your own role (prevents self-lockout).
                    <span title={t("users.cannotChangeOwnRole")}>{u.role}</span>
                  ) : (
                    <select
                      value={u.role}
                      onChange={(e) => onChangeRole(u, e.target.value as UserRole)}
                      aria-label={t("users.th.role")}
                    >
                      {/* Always include the user's current role as an option so
                          an existing SUPERADMIN stays selectable for firm admins. */}
                      {Array.from(new Set([u.role, ...assignableRoles])).map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td>{u.active ? t("common:bool.yes") : t("common:bool.no")}</td>
                <td>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => onDelete(u)}
                    disabled={u.id === claims?.user_id}
                    title={u.id === claims?.user_id ? t("users.cannotDeleteSelfTitle") : t("users.deleteTitle")}
                  >
                    {t("common:actions.delete")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
